import { autoinject, transient, BindingEngine, computedFrom } from "aurelia-framework";
import { BaseEntity } from "../base-entity";
import { CompanyModel, CompanyStepEnum } from "../../../../models/companies/company.models";
import { RouteConfig, Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { NotificationClientService } from "../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { CompanyService } from "../../../../services/modules/company-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { ClaimsRequireTypeEnum } from "../../../../models/basics/basics.models";
import { EntityTypes, type TabModel } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import { EntityItem } from "../entity-item";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { BannerGrouping, DocHybridRule, GetBannerResponse } from "../../../../models/vessels/vessel.models";
import { Reflection } from "../../../../utilities/reflection";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { CoachmarkClientService } from "../../../../services/coachmark/coachmark-client.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { Guid } from "../../../../utilities/guid";
import { CompanyDetails } from "./tabs/overview/tabs/company-details/company-details";
import { CompanyAffiliatedFleet } from "./tabs/overview/tabs/affiliated-fleet/company-affiliated-fleet";
import { CompanyList } from "./tabs/overview/tabs/list/company-list";
import { CompanyDocuments } from "./tabs/overview/tabs/documents/company-documents";
import { CompanyNotes } from "./tabs/overview/tabs/notes/company-notes";
import { CompanyVettingContacts } from "./tabs/overview/tabs/vetting-contacts/company-vetting-contacts";
import { CompanyCrewWelfare } from "./tabs/social/tabs/crew-welfare/company-crew-welfare";
import { CompanyAbandonmentVerification } from "./tabs/social/tabs/abandonment-verification/company-abandonment-verification";
import { CompanyGhgRating } from "./tabs/environment/tabs/ghg-rating/company-ghg-rating";
import { CompanyDocFleet } from "./tabs/governance/tabs/doc-performance/doc-fleet/company-doc-fleet";
import { CompanyDocSubscores } from "./tabs/governance/tabs/doc-performance/doc-subscores/company-doc-subscores";
import { CompanyDocPscIncReport } from "./tabs/governance/tabs/doc-performance/doc-pcs-inc-report/company-doc-pcs-inc-report";
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { UrlHelper } from "../../../../helpers/url-helper";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { Messages } from "../../../../configuration/event-messages.constants";
import { PlatformFeatureKeys } from "@rightship-tech/common-ui/dialogs/upgrade-from-freemium/freemium-feature-info-model";
import { SubscriptionProductCodes } from "../../../../models/customers/customer-subscription.models";
import { ReportsService } from "../../../../services/reports/reports.service";
import { Insights } from "../rightship-insights/common/insights/insights";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { RightStoreService } from "../../../../services/rightstore/rightstore.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { CompBannerCrewWelfareModel } from "@rightship-tech/common-ui/components/comp-banner-crew-welfare/comp-banner-crew-welfare";
import { DocSubscoreGracePeriod } from "@rightship-tech/common-ui/models/companies/company.models";
import Enumerable from "linq";

@autoinject
@transient()
export class Company extends BaseEntity<CompanyModel> {
    readonly: boolean = true;
    entityItem: EntityItem;
    canViewCompanyDocuments: boolean;
    canAddToList: boolean;
    rsManagementAuditRequestNowUrl: string = "/management-audit";

    private canViewCompanyLists: boolean;
    private canViewCrewWelfareTab: boolean;
    private canAccessCompanyInsights: boolean;
    private activateDOCSubscoreForecastReport: boolean;
    public canEditCompanyDetails: boolean;
    public showActivateDocSubscoreBtn: boolean = false;
    public canEditNotes: boolean;
    public canAccessVettingContacts: boolean;
    public canAccessAffiliatedFleet: boolean;
    public showCrewWelfareBadgeInCompany: boolean;
    public showInsightsBetaAndFreeTrialBanner: boolean;
    public banners: GetBannerResponse[] = [];
    public groupedBanners: BannerGrouping[] = [];
    public sanctionBanner: BannerGrouping = null;
    public userCustomerId: number = 0;
    public userBusinessUnitId: number = 0;
    public rightshipUser: boolean;
    public isLimitedSubscription: boolean;
    public maskData: boolean;
    public docSubscoreGracePeriod: DocSubscoreGracePeriod;
    public crewWelfareData: CompBannerCrewWelfareModel;
    public docHybridRules: DocHybridRule[];

    @computedFrom("item.hasDryBMSManagementAudit", "item.hasTMSAManagementAudit", "item.hasCrewWelfareManagementAudit")
    get isManagementAuditBannerActive() {
        return this.item.hasDryBMSManagementAudit || this.item.hasTMSAManagementAudit || this.item.hasCrewWelfareManagementAudit;
    }

    constructor(protected router: Router,
        protected bindingEngine: BindingEngine,
        protected crewWelfare: CompanyCrewWelfare,
        protected abandonmentVerification: CompanyAbandonmentVerification,
        protected i18N: I18N,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected validationsClientService: ValidationsClientService,
        private companyService: CompanyService,
        private companyDetails: CompanyDetails,
        private affiliatedFleet: CompanyAffiliatedFleet,
        private vettingContacts: CompanyVettingContacts,
        private list: CompanyList,
        private documents: CompanyDocuments,
        private notes: CompanyNotes,
        private docFleet: CompanyDocFleet,
        private docSubscores: CompanyDocSubscores,
        private docPscIncReport: CompanyDocPscIncReport,
        private insights: Insights,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private userClientService: UserClientService,
        private dialogService: DialogClientService,
        private userClaimsService: UserClaimsService,
        private coachmarkClientService: CoachmarkClientService,
        private clipboardCopyService: ClipboardCopyService,
        private ghgRating: CompanyGhgRating,
        private signalClientService: SignalClientService,
        private customerService: CustomerService,
        private reportsService: ReportsService,
        private rightStoreService: RightStoreService,
        private configService: ConfigService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);

        this.notes.canEdit = true;
        this.notes.canDelete = true;
        this.canAddToList = this.userClaimsService.hasPermission(Claims.CanAddEntityToLists);
        this.canViewCrewWelfareTab = this.userClaimsService.hasPermission(Claims.CanViewCrewWelfareTab);
        this.showCrewWelfareBadgeInCompany = this.userClaimsService.hasPermission(Claims.ShowCrewWelfareBadgeInCompany);
        this.canViewCompanyLists = this.userClaimsService.hasPermission(Claims.CanViewCompanyLists);
        this.canViewCompanyDocuments = this.userClaimsService.hasPermission(Claims.CanViewCompanyDocuments);
        this.canEditCompanyDetails = this.userClaimsService.hasPermission(Claims.CanEditCompanyDetails);
        this.canEditNotes = this.userClaimsService.hasPermission(Claims.CanViewCompanyNotes);
        this.canAccessVettingContacts = this.userClaimsService.hasPermission(Claims.CanAccessVettingContacts);
        this.rightshipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.showInsightsBetaAndFreeTrialBanner = this.userClaimsService.hasPermission(Claims.ShowInsightsBetaAndFreeTrialBanner);
        this.canAccessCompanyInsights = this.userClaimsService.hasPermission(Claims.CanAccessCompanyInsights);
        this.activateDOCSubscoreForecastReport = this.userClaimsService.hasPermission(Claims.ActivateDOCSubscoreForecastReport);
        this.canAccessAffiliatedFleet = this.userClaimsService.hasPermission(Claims.CanAccessAffiliatedFleet)

        this.signalClientService.subscribe(SignalFunctions.docSubscoreChanged, this.refresh);
    }

    @computedFrom("entityItem.currentStep.title")
    get docSubscoreWarningText(){
        if(this.entityItem?.currentStep?.title != 'Governance'){
            return 'DOC Subscore rules invoked. Please check Governance tab for details.'
        }
    }

    public refresh = async (param) => {
        const companyId = this.getItemId();
        if (param && param.companyIds && param.companyIds.filter(x => x == companyId).length >= 1) {
            await this.getEntity(companyId);
        }
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.docSubscoreChanged, this.refresh);
    }

    async activate(params: any, routeConfig: RouteConfig) {
        await super.activate(params, routeConfig);
    }

    unlockFeature(unlockFeatureKey) {
        if (this.isLimitedSubscription)
            this.eventAggregator.publish(Messages.unlockFeature, unlockFeatureKey)
    }

    openRequestAudit(e) {
        if (!this.isLimitedSubscription) {
            e.stopPropagation();
            window.open(this.rsManagementAuditRequestNowUrl, "_blank");
        }
    }

    attached() {
        super.attached();

        let step: TabModel = null;

        if (this._params.step != undefined) {
            const steps: TabModel[] = this.steps;
            step = steps.filter(s => s.param == this._params.step)[0];
        }

        if (!step) {
            step = this.steps[0];
        }

        //If is freemium, filter the list of steps and get the first item which is not freemium
        if (step.lockedFeature) {
            const steps: TabModel[] = this.steps;
            step = steps.filter(x => !x.lockedFeature)[0];
        }

        this.entityItem.currentStep = step;
        if (!MobileHelper.isMobileDevice()) {
            this.coachmarkClientService.start(RightShipQiRouterConstants.RouteNames.Company);
        }
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Companies;
    }

    protected getSteps(): TabModel[] {

        const queryStringAsObject = UrlHelper.getQueryStringAsObject();
        let queryString = "";
        if (queryStringAsObject["entity-search"]) {
            queryString = "?entity-search=" + queryStringAsObject["entity-search"];
        }

        return [
            {
                title: "Overview",
                step: CompanyStepEnum.CompanyDetails,
                show: true,
                param: 'overview' + queryString,
                children: [
                    {
                        title: 'Company Details',
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.companyDetails,
                        show: true
                    },
                    {
                        title: 'Affiliated Fleet',
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.affiliatedFleet,
                        show: this.canAccessAffiliatedFleet
                    },
                    {
                        title: 'Vetting Contacts',
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.vettingContacts,
                        show: this.canAccessVettingContacts && !this.isLimitedSubscription
                    },
                    {
                        title: 'List',
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.list,
                        show: this.canViewCompanyLists && !this.isLimitedSubscription
                    },
                    {
                        title: () => TitleWithCountHelper.getTitle("Documents", this.item.documentsCount),
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.documents,
                        show: this.canViewCompanyDocuments && !this.isLimitedSubscription
                    },
                    {
                        title: () => TitleWithCountHelper.getTitle("Notes", this.item.privateNotesCount),
                        step: CompanyStepEnum.CompanyDetails,
                        vm: this.notes,
                        show: this.canEditNotes && this.item.companyId > 0 && !this.isLimitedSubscription,
                        getModel: () => ({
                            canSubmit: true,
                            entityTypeId: this.item.entityType,
                            entityId: this.item.companyId,
                            item: this.item
                        })
                    }
                ]
            },
            {
                title: 'Environment',
                step: CompanyStepEnum.GhgPerformance,
                show: () => true,
                param: 'ghg-performance' + queryString,
                children: [
                    {
                        title: 'GHG Rating',
                        step: CompanyStepEnum.GhgPerformance,
                        vm: this.ghgRating,
                        show: true
                    }
                ],
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    this.unlockFeature(PlatformFeatureKeys.companyTabEnvironment);
                }
            },
            {
                title: "Social",
                step: CompanyStepEnum.CrewWelfare,
                show: true,
                param: 'social' + queryString,
                children: [
                    {
                        title: 'Crew welfare',
                        step: 1,
                        vm: this.crewWelfare,
                        show: () => this.canViewCrewWelfareTab,
                        getModel: () => this.crewWelfareData
                    },
                    {
                        title: 'Abandonment Verification',
                        step: 2,
                        vm: this.abandonmentVerification,
                        show: () => this.canViewCrewWelfareTab,
                    }
                ],
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    this.unlockFeature(PlatformFeatureKeys.companyTabSocial);
                }
            },
            {
                title: 'Governance',
                step: CompanyStepEnum.DocPerformance,
                show: true,
                param: 'doc-performance' + queryString,
                children: [
                    {
                        title: 'Doc performance',
                        step: 1,
                        show: true,
                        children: [
                            {
                                title: "DOC Fleet",
                                step: 1,
                                vm: this.docFleet,
                                show: this.userClaimsService.hasPermissions([Claims.CanAccessDOCFleetOverview, Claims.CanAccessDOCFleetTable], ClaimsRequireTypeEnum.RequireAny)
                            },
                            {
                                title: "DOC Subscores",
                                vm: this.docSubscores,
                                step: 2,
                                show: this.userClaimsService.hasPermission(Claims.CanAccessSafetyScoreDocScores)
                            },
                            {
                                title: "DOC INC/PSC Report (RS Only)",
                                step: 3,
                                vm: this.docPscIncReport,
                                show: this.userClaimsService.hasPermission(Claims.DocAndVesselPerformanceReports)
                            }

                        ]
                    }
                ],
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    this.unlockFeature(PlatformFeatureKeys.companyTabGovernance);
                }
            },
            {
                title: 'Insights',
                step: CompanyStepEnum.Insights,
                show: this.canAccessCompanyInsights,
                param: 'insights' + queryString,
                vm: this.insights,
                class: this.showInsightsBetaAndFreeTrialBanner ? 'insights' : '',
                getModel: () => ({
                    entityId: this.item.companyId,
                    entityTypeId: EntityTypes.Company,
                })
            }
        ]
    }

    protected async getItem(id: number): Promise<CompanyModel> {

        this.getCompanyBanners(id);
        const response = await this.companyService.getCompany(id)
        this.companyDetails.certificates = response.certificates;
        this.crewWelfareData = {
            crewWelfareStatus: response.crewWelfareStatus,
            docCompanyName: response.company.fullName,
            docCompanyId: response.company.companyId,
            crewWelfareValidUntil: response.crewWelfareValidUntil,
            isLegacyCrewWelfare: response.isLegacyCrewWelfare,
            crewWelfareGuid: response.crewWelfareGuid,

        };
        const user = await this.manageRightShipUsersClientService.getBusinessUnitUser(this.userClientService.currentUser.id);
        if (user && user.businessUnit && user.businessUnit.customerId) {
            this.userCustomerId = user.businessUnit.customerId;
            this.userBusinessUnitId = user.businessUnit.businessUnitId;
            this.maskData = await this.companyService.getMaskDataForCompany(response.company.companyId);
            this.affiliatedFleet.maskData = this.maskData;
            this.isLimitedSubscription = await this.manageRightShipUsersClientService.isLimitedCustomerSubscription(SubscriptionProductCodes.informationServices);
            this.reportsService.isCompanyReportPurchased('doc-subscore-forecast', response.company.companyId, true).then(isReportPurchasedResponse => {
                this.showActivateDocSubscoreBtn = !isReportPurchasedResponse.anyFulfilledOrders &&
                    !isReportPurchasedResponse.anyPendingOrders &&
                    response.company.customerId == user.businessUnit.customerId && this.activateDOCSubscoreForecastReport;
            });
        }

        this.docSubscoreGracePeriod = {
            companyName: response.company.fullName,
            numberOfVesselsHavingIncidentGracePeriod: response.company.numberOfVesselsHavingIncidentGracePeriod,
        }
        this.docHybridRules = response.company.docHybridRules;
        
        return response.company;
    }

    private getCompanyBanners = (id: number = null) => {
        this.companyService.getCompanyBanners(id ? id : this.getItemId()).then(x => {

            this.banners = x.filter(x => x.internalName != 'sanctioned');
            this.groupedBanners = [];

            Enumerable.from(x).groupBy(x => x.internalName).forEach(groupedBanner => {

                const bannerGroup = <BannerGrouping>{
                    response: groupedBanner.toArray(),
                    internalName: groupedBanner.toArray()[0].internalName,
                    displayName: groupedBanner.toArray()[0].displayName
                }

                this.groupedBanners.push(bannerGroup);
            });

            let sanctionGroup = this.groupedBanners.filter(x => x.internalName == 'sanctioned')[0];
            if (sanctionGroup)
            {
                sanctionGroup = this.groupedBanners.splice(this.groupedBanners.indexOf(sanctionGroup), 1)[0];
                for (let i = 0; i < sanctionGroup.response.length; i++) {

                    //If there is one of the banners because the company is sanctioned, then the display name takes priority over associated sanctions
                    sanctionGroup.displayName = sanctionGroup.displayName == "sanctioned company" ? "sanctioned company" : sanctionGroup.response[i].displayName;
                }

                this.sanctionBanner = sanctionGroup;
            }
            
            this.companyDetails.banners = this.banners;
        });
    }

    protected getItemId(): number {
        return this.item ? this.item.companyId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        throw new Error("Method not implemented.");
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Company;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.Company;
    }

    protected initializeItemForCreate(): CompanyModel | Promise<CompanyModel> {
        const company: CompanyModel = {
            companyId: null,
            address: null,
            companyRegistrationNumber: null,
            controlCountryId: null,
            countryId: null,
            description: null,
            email: null,
            facsimile: null,
            foundedDate: null,
            fullName: null,
            owCode: null,
            logoUrl: null,
            parentCompanyId: null,
            registrationCountryId: null,
            residence: null,
            shortCompanyName: null,
            size: null,
            structuredAddress: null,
            telephone: null,
            telex: null,
            website: null,
            countryName: null,
            countryCode: null,
            controlCountryName: null,
            registrationCountryName: null,
            hasDryBMSManagementAudit: null,
            hasTMSAManagementAudit: null,
            hasCrewWelfareManagementAudit: null,
            identifier: Guid.generateGuid(),
            isUnknown: null,
            numberOfVesselsHavingIncidentGracePeriod: null,
            docHybridRules: null
        }

        return company;
    }

    public onStepClicked = (step: TabModel) => {
        if (!step) {
            return;
        }

        if (step.param) {
            const params = this.router.currentInstruction.params;
            params.step = step.param;

            const state = window.history.state;

            this.router.navigateToRoute(this.router.currentInstruction.config.name, params, {
                trigger: false,
                replace: true,
            });

            window.history.replaceState(state, "Company");
        }
    }

    public addToList() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.AddToList,
            {
                entityId: this.item.companyId,
                entityType: EntityTypes.Company,
                onComplete: () => {
                    this.getCompanyBanners();
                    this.getCompanyCertificates();
                    this.getCompanyListFlags();
                    this.list.reload();
                },
            }, true, false, false, 'right');
    }

    public async activateDocSubscoreReport() {
        const queryParams = this.configService.config.environment.toLowerCase() == "production" ?
            `companyId=${this.item.companyId}` :
            `companyId=${this.item.companyId}&env=${this.configService.config.environment}`;

        const customerModel = this.userCustomerId > 0 ? await this.customerService.getCustomer(this.userCustomerId) : null;
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog, {
            userName: this.userClientService.currentUser.email,
            customerNumber: this.userBusinessUnitId,
            productNumber: 'doc-subscore-forecast',
            queryParams: queryParams,
            canCustomerInvoice: customerModel?.customer?.canInvoice ?? true,
            afterPurchase: (async (url: string) => {
                const orderId = new URL(url).pathname.split('order/').pop()?.split(/[/|?]/)?.find(Boolean);
                const response = await this.rightStoreService.saveOrderMeta(orderId, JSON.stringify({ "companyId": this.item.companyId }));
                const orderUrlResponse = await this.rightStoreService.getOrderUrl(response.orderGuid);
                if (orderUrlResponse.url.startsWith(location.origin)) {
                    this.router.navigate(orderUrlResponse.url.replace(location.origin, ""), { replace: true, trigger: true });
                }
                else {
                    location.href = orderUrlResponse.url;
                }
            }).bind(this)
        }, true, false, false, 'right');
    }

    private getCompanyListFlags = (id: number = null) => {
        this.companyService.getCompanyListFlags(id ? id : this.getItemId()).then(x => {
            this.companyDetails.item.hasCompanyAbandonmentofSeafarersOlderThanFiveYears = x.hasCompanyAbandonmentofSeafarersOlderThanFiveYears;
            this.companyDetails.item.hasTMSAManagementAudit = x.hasTMSAManagementAudit;
            this.companyDetails.item.hasDryBMSManagementAudit = x.hasDryBMSManagementAudit;
            this.companyDetails.item.hasCrewWelfareManagementAudit = x.hasCrewWelfareManagementAudit;
        });
    }

    private getCompanyCertificates = (id: number = null) => {
        this.companyService.getCompanyCertificates(id ? id : this.getItemId()).then(x => {
            this.companyDetails.certificates = x;
        });
    }

    @computedFrom(Reflection.getPropertyPath<Company>(c => c.router.currentInstruction))
    public get previousInstructionTitle(): string {
        let title = this.router.history.getState("previousRouteName");

        if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
            return title;

        const prevInstruction = this.router.currentInstruction.previousInstruction;
        title = prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');

        this.router.history.setState("previousRouteName", title);

        return title;
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    getDocSubScoreReport() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
            {
                companyId: this.item.companyId,
                businessUnitId: this.userBusinessUnitId,
                productCode: 'doc-subscore-report'
            }, true, false, false, 'right');
    }

    getBannerText(banner: BannerGrouping) {
        
        if(!banner || !banner.response || banner.response.length == 0)
            return '';

        if(banner.internalName == 'restricted')
            return (banner.response[0].businessUnit? (banner.response[0].businessUnit+' '):'' ) + "Restricted List";
        else if(banner.internalName == 'banned')
            return (banner.response[0].businessUnit? (banner.response[0].businessUnit+' '):'') + "Banned";
        else if(banner.internalName == 'sanctioned')
            return (banner.response[0].businessUnit? (banner.response[0].businessUnit+' '):'') + "Sanctioned";
        else if(banner.internalName == 'external-customer-sanctioned')
            return (banner.response[0].businessUnit? (banner.response[0].businessUnit+' '):'') + "External Customer Sanctions & Compliance";
        else if(banner.internalName == 'customer-sanctioned')
            return (banner.response[0].businessUnit? (banner.response[0].businessUnit+' '):'') + "Customer Sanctions & Compliance";
        else if(banner.internalName.toLowerCase() == 'customerrestrictedcompanies')
            return "Customer Restricted Companies";
        else
            return banner.response[0].displayName;
    }
}
